/**
 * Client API verso il backend Blagomail con auto-refresh JWT.
 */
const API_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

const ACCESS_KEY = 'blagomail.access';
const REFRESH_KEY = 'blagomail.refresh';

export const tokenStore = {
  getAccess: () => localStorage.getItem(ACCESS_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown
  ) {
    super(message);
  }
}

interface RequestOptions extends RequestInit {
  retried?: boolean;
}

async function rawRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (!(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  const access = tokenStore.getAccess();
  if (access) headers.set('Authorization', `Bearer ${access}`);

  const res = await fetch(`${API_URL}${path}`, { ...options, headers });

  if (res.status === 401 && !options.retried) {
    const refresh = tokenStore.getRefresh();
    if (refresh) {
      const refreshed = await tryRefresh(refresh);
      if (refreshed) return rawRequest<T>(path, { ...options, retried: true });
    }
    tokenStore.clear();
    if (typeof window !== 'undefined') window.location.href = '/login';
  }

  if (!res.ok) {
    // Il backend risponde { error: "CODICE", message: "...", details?: ... }
    let body: { error?: string; message?: string; details?: unknown } = {};
    try { body = await res.json(); } catch { /* */ }
    throw new ApiError(
      res.status,
      typeof body.error === 'string' ? body.error : 'HTTP_ERROR',
      body.message ?? `HTTP ${res.status}`,
      body.details
    );
  }

  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  if (!ct.includes('application/json')) return (await res.blob()) as unknown as T;
  return (await res.json()) as T;
}

async function tryRefresh(refreshToken: string): Promise<boolean> {
  try {
    const res = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) return false;
    const data = (await res.json()) as { tokens: { accessToken: string; refreshToken: string } };
    tokenStore.set(data.tokens.accessToken, data.tokens.refreshToken);
    return true;
  } catch { return false; }
}

export const api = {
  get: <T>(path: string) => rawRequest<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) =>
    rawRequest<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    rawRequest<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    rawRequest<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => rawRequest<T>(path, { method: 'DELETE' }),
};

export { API_URL };
